import React from 'react';
import { useTranslation } from 'react-i18next';

import {
    buildStPortalRedirectUrl,
    sendStPortalNotice,
    stPortalDomain,
} from '../../utils/stCorporateRedirect';
import './StRedirectNotice.css';

const REDIRECT_DELAY_MS = 6000;
const TICK_MS = 250;

type MailStatus = 'sending' | 'sent' | 'failed';

interface StRedirectNoticeProps {
    /** Adresse corporate saisie sur l'instance publique. */
    email: string;
    /** Permet de revenir au formulaire (ex. mauvaise adresse saisie). */
    onCancel?: () => void;
}

/**
 * Interstitiel affiché quand une adresse corporate est saisie sur l'instance publique.
 *
 * Explique pourquoi aucun compte n'est créé ici, envoie le lien du portail par email,
 * puis redirige. L'utilisateur garde la main : il peut partir immédiatement ou revenir.
 */
const StRedirectNotice: React.FC<StRedirectNoticeProps> = ({ email, onCancel }) => {
    const { t, i18n } = useTranslation();
    const [mailStatus, setMailStatus] = React.useState<MailStatus>('sending');
    const [remainingMs, setRemainingMs] = React.useState(REDIRECT_DELAY_MS);
    const primaryButtonRef = React.useRef<HTMLButtonElement>(null);
    const cardRef = React.useRef<HTMLDivElement>(null);
    const hasLeftRef = React.useRef(false);

    const portalUrl = React.useMemo(
        () => buildStPortalRedirectUrl(typeof window !== 'undefined' ? window.location.search : ''),
        []
    );
    const portalDomain = React.useMemo(() => stPortalDomain(), []);

    const goToPortal = React.useCallback(() => {
        if (hasLeftRef.current) return;
        hasLeftRef.current = true;
        window.location.replace(portalUrl);
    }, [portalUrl]);

    // L'email part avant la navigation : window.location.replace annulerait la requête.
    React.useEffect(() => {
        let cancelled = false;
        sendStPortalNotice(email, i18n.language)
            .then((ok) => {
                if (!cancelled) setMailStatus(ok ? 'sent' : 'failed');
            })
            .catch(() => {
                if (!cancelled) setMailStatus('failed');
            });
        return () => {
            cancelled = true;
        };
    }, [email, i18n.language]);

    React.useEffect(() => {
        primaryButtonRef.current?.focus();
    }, []);

    React.useEffect(() => {
        const startedAt = Date.now();
        const timer = window.setInterval(() => {
            const left = REDIRECT_DELAY_MS - (Date.now() - startedAt);
            if (left <= 0) {
                window.clearInterval(timer);
                setRemainingMs(0);
                goToPortal();
                return;
            }
            setRemainingMs(left);
        }, TICK_MS);

        return () => window.clearInterval(timer);
    }, [goToPortal]);

    // Escape ramène au formulaire, et le focus reste dans la boîte tant qu'elle est ouverte
    // (le formulaire derrière est annoncé inerte par aria-modal).
    React.useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && onCancel) {
                onCancel();
                return;
            }
            if (event.key !== 'Tab' || !cardRef.current) return;

            const focusables = cardRef.current.querySelectorAll<HTMLElement>('button');
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onCancel]);

    const secondsLeft = Math.ceil(remainingMs / 1000);
    const progress = Math.max(0, Math.min(100, (remainingMs / REDIRECT_DELAY_MS) * 100));

    return (
        <div className="st-redirect-overlay">
            <div
                ref={cardRef}
                className="st-redirect-card"
                role="dialog"
                aria-modal="true"
                aria-labelledby="st-redirect-title"
                aria-describedby="st-redirect-description"
            >
                <span className="st-redirect-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        <path d="m9 12 2 2 4-4" />
                    </svg>
                </span>

                <h2 className="st-redirect-title" id="st-redirect-title">
                    {t('views.auth.ssoRedirectTitle')}
                </h2>

                <p className="st-redirect-text" id="st-redirect-description">
                    {t('views.auth.ssoRedirectBody', { email })}
                </p>
                <p className="st-redirect-text">
                    {t('views.auth.ssoRedirectDestination')}{' '}
                    <span className="st-redirect-domain">{portalDomain}</span>
                </p>

                <div
                    className={`st-redirect-mail-status${mailStatus === 'sent' ? ' st-redirect-mail-status--sent' : ''}`}
                    role="status"
                    aria-live="polite"
                >
                    {mailStatus === 'sent' ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="2" y="4" width="20" height="16" rx="2" />
                            <path d="m22 7-10 6L2 7" />
                        </svg>
                    )}
                    <span>
                        {mailStatus === 'sending' && t('views.auth.ssoRedirectMailSending')}
                        {mailStatus === 'sent' && t('views.auth.ssoRedirectMailSent', { email })}
                        {mailStatus === 'failed' && t('views.auth.ssoRedirectMailFailed')}
                    </span>
                </div>

                <div
                    className="st-redirect-progress"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(100 - progress)}
                    aria-label={t('views.auth.ssoRedirectTitle')}
                >
                    <div className="st-redirect-progress-bar" style={{ width: `${progress}%` }} />
                </div>

                <div className="st-redirect-actions">
                    <button
                        type="button"
                        ref={primaryButtonRef}
                        className="st-redirect-btn st-redirect-btn--primary"
                        onClick={goToPortal}
                    >
                        {t('views.auth.ssoRedirectGoNow')}
                    </button>
                    {onCancel && (
                        <button
                            type="button"
                            className="st-redirect-btn st-redirect-btn--ghost"
                            onClick={onCancel}
                        >
                            {t('views.auth.ssoRedirectUseAnotherEmail')}
                        </button>
                    )}
                </div>

                <p className="st-redirect-countdown">
                    {t('views.auth.ssoRedirectCountdown', { count: secondsLeft })}
                </p>
            </div>
        </div>
    );
};

export default StRedirectNotice;
